import { type PhaseBandNode } from '@/components/diagrams/FlowChart/types';
import {
  getPhaseBandNodes,
  type PhaseBand,
  type PhaseGeometry,
} from '@/components/diagrams/FlowChart/utils/phaseLayout';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import {
  STAGE_BAND_HEIGHT,
  STAGE_BAND_OFFSET,
  STAGE_GAP,
} from './diagramLayout.config';
import { type ReactFlowDiagramNode } from './reactFlowDiagram';

/**
 * Returns how the tiles of a diagram occupy its stages. The stages are the
 * phases of the diagram, as the death sections are the phases of a
 * presentation diagram. The phase of a tile is the index of its stage. A tile
 * without a stage is in no phase. React Flow measures the tile sizes.
 */
export function getStageGeometry(
  diagram: DiagramBuilder_DiagramFragment
): PhaseGeometry<ReactFlowDiagramNode> {
  const indexByStageID = new Map(
    diagram.content.stages.map((stage, index) => [stage.id, index])
  );
  return {
    phaseOf: (node) => indexByStageID.get(node.data.item.stageId ?? ''),
    sizeOf: (node) => node.measured?.height ?? 0,
  };
}

/**
 * Returns a band for the death event before each stage that has tiles, as a
 * presentation phase band does.
 *
 * The first stage has no death event before it. A stage without a death
 * event has no band. An empty stage has no tiles to put
 * a band against, so it has no band. The band before the next stage covers
 * the same boundary.
 *
 * When `onEditStage` is set, a click on a band label calls it with the ID of
 * the stage after the band.
 */
function getStageBands(
  diagram: DiagramBuilder_DiagramFragment,
  nodes: ReactFlowDiagramNode[],
  onEditStage?: (stageID: string) => void
): PhaseBand[] {
  return diagram.content.stages.flatMap((stage, index) => {
    const label = stage.deathEvent;
    const hasTiles = nodes.some((node) => node.data.item.stageId === stage.id);
    if (index === 0 || !label || !hasTiles) return [];
    return [
      {
        label,
        belowPhaseIndex: index - 1,
        onEdit: onEditStage && (() => onEditStage(stage.id)),
      },
    ];
  });
}

/**
 * Returns the shared phase bands between the stages, at the current tile
 * positions. A band follows the earlier stage at STAGE_BAND_OFFSET. When the
 * stages come closer, it goes to the middle of the space between them.
 * `onEditStage` makes the band labels editable, as in `getStageBands`.
 */
export function getStageBandNodes(
  diagram: DiagramBuilder_DiagramFragment,
  nodes: ReactFlowDiagramNode[],
  onEditStage?: (stageID: string) => void
): PhaseBandNode[] {
  return getPhaseBandNodes({
    nodes,
    bands: getStageBands(diagram, nodes, onEditStage),
    geometry: getStageGeometry(diagram),
    widthOf: (node) => node.measured?.width ?? node.width ?? 0,
    height: STAGE_BAND_HEIGHT,
    // The automatic layout keeps this space at the left of the tiles. The
    // fit view includes it, so the band labels stay clear of the tiles.
    gutter: STAGE_GAP,
    boundary: { maxOffset: STAGE_BAND_OFFSET },
  });
}
